import Cookies from 'js-cookie';
import { v1 } from 'uuid';
import { isAutomatedAgent } from './BotDetection';
const COOKIE_ID_KEY = 'cookie_id';
const USER_ID_KEY = 'auto_user_id';
// ADV-263: session-scoped correlation id, deliberately separate from COOKIE_ID_KEY (365-day
// visitor identity, doubles as the anonymous-favorites ownership key — see FavoriteRepository).
// Host-scoped (no `domain=`), matching cookie_id's security posture rather than the `marketing`
// cookie's cross-subdomain one — SRL/VDP/Checkout are all same-host fe-main routes, so no
// cross-subdomain need, and this avoids reopening the domain-widening security question.
const SESSION_ID_KEY = 'session_id';
const SESSION_EXPIRES_MINUTES = 30;
const COOKIE_EXPIRES = 365;
const COOKIE_CONFIG_LS_KEY = 'cookieConfig';
class CookieUtils {
    constructor() {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "userId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "setCookieId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => Cookies.set(COOKIE_ID_KEY, v1(), { expires: COOKIE_EXPIRES })
        });
        Object.defineProperty(this, "getCookieId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => Cookies.get(COOKIE_ID_KEY)
        });
        Object.defineProperty(this, "resolveCookieId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.id) {
                    this.setCookieId();
                    this.id = this.getCookieId();
                }
                return this.id;
            }
        });
        // ADV-263: sliding 30-minute window — re-set on EVERY call (not just when absent) so
        // internal navigation within a visit extends the session instead of ever starting a new
        // one mid-visit. Mirrors resolveCookieId's mint-on-first-read pattern otherwise.
        // Crawlers get no session (see BotDetection.ts). Their impressions still reach BigQuery as
        // rows without session_id, but they never become a visit.
        Object.defineProperty(this, "resolveSessionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (isAutomatedAgent())
                    return undefined;
                const id = Cookies.get(SESSION_ID_KEY) || v1();
                const expiresAt = new Date();
                expiresAt.setMinutes(expiresAt.getMinutes() + SESSION_EXPIRES_MINUTES);
                Cookies.set(SESSION_ID_KEY, id, { expires: expiresAt });
                return id;
            }
        });
        // ADV-263: derives a simple per-category true/false from the CookieModal's own consent
        // store (`@bytebrand/fe-ui-core-autobahn`'s `localStorage['cookieConfig']` — `{ marketing:
        // string[], analytics: string[] }`, arrays of DISABLED vendor-cookie names per category).
        // Deliberately reads localStorage directly rather than importing that package's
        // COOKIE_SCHEMA vendor list, to avoid a new cross-package coupling for a simple boolean —
        // treats "any vendor in a category still disabled" as consent NOT granted for that
        // category (the conservative reading). No decision made yet (key absent) also reads as
        // not-granted, not "assume yes." Lives here (not CommonUtils.ts) so BaseHttp.ts can read it
        // without adding a new import edge into CommonUtils.ts's module graph.
        // Documents consent on outgoing events — does not itself gate capture; see the ADV-263 plan
        // doc's compliance open question about whether raw marketing-cookie capture should be gated.
        Object.defineProperty(this, "getConsentState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                try {
                    const raw = localStorage.getItem(COOKIE_CONFIG_LS_KEY);
                    if (!raw)
                        return { marketingConsent: false, analyticsConsent: false };
                    const cfg = JSON.parse(raw);
                    return {
                        marketingConsent: Array.isArray(cfg.marketing) ? cfg.marketing.length === 0 : false,
                        analyticsConsent: Array.isArray(cfg.analytics) ? cfg.analytics.length === 0 : false,
                    };
                }
                catch (e) {
                    return { marketingConsent: false, analyticsConsent: false };
                }
            }
        });
        Object.defineProperty(this, "setUserID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                Cookies.set(USER_ID_KEY, id);
                this.userId = id;
            }
        });
        Object.defineProperty(this, "getUserID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => Cookies.get(USER_ID_KEY)
        });
        Object.defineProperty(this, "resolveUserID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.userId) {
                    return;
                }
                return this.userId;
            }
        });
        Object.defineProperty(this, "removeUserID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                Cookies.remove(USER_ID_KEY);
                this.userId = null;
            }
        });
        this.id = Cookies.get(COOKIE_ID_KEY);
        this.userId = Cookies.get(USER_ID_KEY);
    }
}
export default new CookieUtils();
